﻿import { CornerIcon, FoulIcon, FreeKickIcon, RedCardIcon, YellowCardIcon } from '@/common/assets';
import { PlatformSection } from '@/common/components';
import { useNewTranslations } from '@/common/translations';
import { MatchLabels, SectionSeparator } from '@/ui/components';
import {
  RowComparisonEvent,
  RowLabelAndCircularGraph,
  RowLabelAndNumbers,
  RowTeamShotsPerGame,
  RowTeamGoalsWithJerseys,
  StatsShotsChart,
} from '../../components';
import { usePrematchData } from '../../hooks';
import toFixedOrInteger from '../../utils/toFixedOrInteger';

const rowHeightClassName = 'h-[40px]';
// Should be the same as circular graph size.
const columnWidthClassName = 'w-[54px]';

export default function SeasonStatisticsSection() {
  const { teams, statistics } = usePrematchData();
  const translate = useNewTranslations();

  if (!statistics?.seasonStatistics) {
    return (
      <PlatformSection
        name={translate('PreMatchStats.Statistics')}
        contentClassName="py-8 flex flex-col gap-2 items-center text-center"
      >
        <span className="font-unibet text-[24px] leading-[24px] text-pmw-label-primary-light dark:text-pmw-label-primary-dark">
          {translate('PreMatchStats.StatisticsWillBeAvailableSoon')}
        </span>
        <span className="whitespace-pre-line font-robotoRegular text-[12px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
          {translate('PreMatchStats.StatisticsWillBeAvailableSoonDetails')}
          {'\n\n'}
          {translate('PreMatchStats.CheckafterFirstMatches')}
        </span>
      </PlatformSection>
    );
  }

  const { seasonStatistics } = statistics;
  const { prevYear, currentYear, leagueName, home: homeStats, away: awayStats } = seasonStatistics;
  const yearsRange = prevYear ? `${prevYear} / ${currentYear}` : `${currentYear}`;

  return (
    <PlatformSection
      name={`${translate('PreMatchStats.Statistics')} - ${yearsRange}`}
      subtitle={leagueName || ''}
      contentClassName="p-0 flex flex-col gap-2"
    >
      <div className="p-2 flex flex-col gap-2">
        <MatchLabels homeName={teams.home.name} awayName={teams.away.name} />
        <RowLabelAndNumbers
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.GamesPlayed')}
          homeNumber={homeStats.gamesPlayed}
          awayNumber={awayStats.gamesPlayed}
        />
        <RowLabelAndCircularGraph
          label={`% ${translate('PreMatchStats.Victory')}`}
          homeNumber={homeStats.winRate / 100}
          awayNumber={awayStats.winRate / 100}
        />
        <RowLabelAndCircularGraph
          label={`% ${translate('PreMatchStats.AveragePossession')}`}
          homeNumber={homeStats.possession / 100}
          awayNumber={awayStats.possession / 100}
        />
      </div>
      <SectionSeparator />
      <div className="p-2 flex flex-col gap-2">
        <RowTeamGoalsWithJerseys
          homeNumber={homeStats.totalGoals}
          awayNumber={awayStats.totalGoals}
          homeJerseySrc={teams.home.jersey}
          awayJerseySrc={teams.away.jersey}
        />
        <RowLabelAndNumbers
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.ScoredGame')}
          homeNumber={toFixedOrInteger(homeStats.goalsScored, 1)}
          awayNumber={toFixedOrInteger(awayStats.goalsScored, 1)}
        />
        <RowLabelAndNumbers
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.ConcededGame')}
          homeNumber={toFixedOrInteger(homeStats.goalsConceded, 1)}
          awayNumber={toFixedOrInteger(awayStats.goalsConceded, 1)}
        />
      </div>
      <SectionSeparator />
      <div className="p-2 flex flex-col gap-2">
        <RowLabelAndCircularGraph
          label={translate('PreMatchStats.OverGoals15')}
          homeNumber={homeStats.over15Goals / 100}
          awayNumber={awayStats.over15Goals / 100}
        />
        <RowLabelAndCircularGraph
          label={translate('PreMatchStats.OverGoals25')}
          homeNumber={homeStats.over25Goals / 100}
          awayNumber={awayStats.over25Goals / 100}
        />
        <RowLabelAndCircularGraph
          label={translate('PreMatchStats.Over05HT')}
          homeNumber={homeStats.over05GoalsHT / 100}
          awayNumber={awayStats.over05GoalsHT / 100}
        />
        <RowLabelAndCircularGraph
          label={translate('PreMatchStats.Over15HT')}
          homeNumber={homeStats.over15GoalsHT / 100}
          awayNumber={awayStats.over15GoalsHT / 100}
        />
      </div>
      <SectionSeparator />
      <div className="p-2 flex flex-col gap-4">
        <RowTeamShotsPerGame
          homeNumber={toFixedOrInteger(homeStats.shotsPerGame, 1)}
          awayNumber={toFixedOrInteger(awayStats.shotsPerGame, 1)}
          homeJerseySrc={teams.home.jersey}
          awayJerseySrc={teams.away.jersey}
        />
        <StatsShotsChart
          className="pb-4"
          homeOffTarget={homeStats.shotsOffTarget}
          homeOnTarget={homeStats.shotsOnTarget}
          awayOnTarget={awayStats.shotsOnTarget}
          awayOffTarget={awayStats.shotsOffTarget}
          homeConcededOffTarget={homeStats.shotsConcededOffTarget}
          homeConcededOnTarget={homeStats.shotsConcededOnTarget}
          awayConcededOnTarget={awayStats.shotsConcededOnTarget}
          awayConcededOffTarget={awayStats.shotsConcededOffTarget}
        />
      </div>
      <SectionSeparator />
      <div className="p-2 flex flex-col gap-2">
        <div className="flex justify-center font-robotoRegular text-[10px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
          {translate('PreMatchStats.AverageMatchStatistics')}
        </div>
        <RowComparisonEvent
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.YellowCards')}
          homeNumber={toFixedOrInteger(homeStats.yellowCards, 1)}
          awayNumber={toFixedOrInteger(awayStats.yellowCards, 1)}
          icon={<YellowCardIcon />}
        />
        <RowComparisonEvent
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.RedCards')}
          homeNumber={toFixedOrInteger(homeStats.redCards, 1)}
          awayNumber={toFixedOrInteger(awayStats.redCards, 1)}
          icon={<RedCardIcon />}
        />
        <RowComparisonEvent
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.Freekicks')}
          homeNumber={toFixedOrInteger(homeStats.freeKicks, 1)}
          awayNumber={toFixedOrInteger(awayStats.freeKicks, 1)}
          icon={<FreeKickIcon />}
        />
        <RowComparisonEvent
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.Corners')}
          homeNumber={toFixedOrInteger(homeStats.corners, 1)}
          awayNumber={toFixedOrInteger(awayStats.corners, 1)}
          icon={<CornerIcon />}
        />
        <RowComparisonEvent
          className={rowHeightClassName}
          numberColumnClassName={columnWidthClassName}
          label={translate('PreMatchStats.Fouls')}
          homeNumber={toFixedOrInteger(homeStats.fouls, 1)}
          awayNumber={toFixedOrInteger(awayStats.fouls, 1)}
          icon={<FoulIcon />}
        />
      </div>
    </PlatformSection>
  );
}
